import React, { useState, useEffect } from 'react';
import { 
  TruckIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  StarIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Vehicle, getVehicles, createVehicle, updateVehicle, deleteVehicle, setDefaultVehicle } from '@/services/vehicles';

interface VehicleManagerProps {
  onVehicleSelect?: (vehicle: Vehicle) => void;
  selectedVehicleId?: string;
  showSelectOnly?: boolean;
}

const VehicleManager: React.FC<VehicleManagerProps> = ({ 
  onVehicleSelect, 
  selectedVehicleId, 
  showSelectOnly = false 
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    plate: '',
    color: '',
    vin: '',
    nickname: '',
    isDefault: false
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setIsLoading(true);
      setError('');
      const vehiclesData = await getVehicles();
      setVehicles(vehiclesData);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Error al cargar los vehículos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      
      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, formData);
      } else {
        await createVehicle(formData);
      }
      
      await loadVehicles();
      resetForm();
    } catch (err) {
      console.error('Error saving vehicle:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el vehículo');
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      plate: vehicle.plate,
      color: vehicle.color || '',
      vin: vehicle.vin || '',
      nickname: vehicle.nickname || '',
      isDefault: vehicle.isDefault
    });
    setShowForm(true);
  };

  const handleDelete = async (vehicle: Vehicle) => {
    const vehicleName = vehicle.nickname || `${vehicle.brand} ${vehicle.model}`;
    if (!confirm(`¿Estás seguro de que quieres eliminar el vehículo "${vehicleName}" (${vehicle.plate})?`)) {
      return;
    }

    try {
      setError('');
      await deleteVehicle(vehicle.id);
      await loadVehicles();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar el vehículo');
    }
  };

  const handleSetDefault = async (vehicle: Vehicle) => {
    try {
      setError('');
      await setDefaultVehicle(vehicle.id);
      await loadVehicles();
    } catch (err) {
      console.error('Error setting default vehicle:', err);
      setError(err instanceof Error ? err.message : 'Error al establecer vehículo predeterminado');
    }
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      plate: '',
      color: '',
      vin: '',
      nickname: '',
      isDefault: false
    });
    setEditingVehicle(null);
    setShowForm(false);
  };

  const handleVehicleSelect = (vehicle: Vehicle) => {
    if (onVehicleSelect) {
      onVehicleSelect(vehicle);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Cargando vehículos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <TruckIcon className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            {showSelectOnly ? 'Seleccionar Vehículo' : 'Mis Vehículos'}
          </h2>
        </div>
        
        {!showSelectOnly && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Agregar Vehículo</span>
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <XMarkIcon className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Vehicles List */}
      {vehicles.length === 0 ? (
        <div className="text-center py-8">
          <TruckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No tienes vehículos registrados
          </h3>
          <p className="text-gray-600 mb-4">
            {showSelectOnly 
              ? 'Necesitas agregar un vehículo para continuar'
              : 'Agrega tu primer vehículo para comenzar'
            }
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            Agregar Vehículo
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {vehicles.map((vehicle) => (
            <div
              key={vehicle.id}
              className={`bg-white rounded-lg border-2 p-4 transition-all ${
                selectedVehicleId === vehicle.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              } ${showSelectOnly ? 'cursor-pointer' : ''}`}
              onClick={() => showSelectOnly && handleVehicleSelect(vehicle)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TruckIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900">
                        {vehicle.nickname || `${vehicle.brand} ${vehicle.model}`}
                      </h3>
                      {vehicle.isDefault && (
                        <StarIcon className="w-5 h-5 text-yellow-500 fill-current" />
                      )}
                    </div>
                    {vehicle.nickname && (
                      <p className="text-sm text-gray-600">
                        {vehicle.brand} {vehicle.model}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      {vehicle.year} • {vehicle.plate}
                    </p>
                    {vehicle.color && (
                      <p className="text-xs text-gray-500">
                        Color: {vehicle.color}
                      </p>
                    )}
                  </div>
                </div>

                {!showSelectOnly && (
                  <div className="flex items-center space-x-2">
                    {!vehicle.isDefault && (
                      <button
                        onClick={() => handleSetDefault(vehicle)}
                        className="p-2 text-gray-400 hover:text-yellow-500 transition-colors"
                        title="Establecer como predeterminado"
                      >
                        <StarIcon className="w-5 h-5" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleEdit(vehicle)}
                      className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Editar vehículo"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(vehicle)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Eliminar vehículo"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {showSelectOnly && selectedVehicleId === vehicle.id && (
                  <CheckIcon className="w-6 h-6 text-blue-500" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Botón para agregar otro vehículo en modo selección */}
      {showSelectOnly && vehicles.length > 0 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowForm(true)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            + Agregar otro vehículo
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingVehicle ? 'Editar Vehículo' : 'Agregar Vehículo'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apodo del Vehículo
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Ej: Mi Toyota, El Carro Azul, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nombre personalizado para identificar fácilmente tu vehículo
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca *
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modelo *
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Año *
                  </label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Placa *
                  </label>
                  <input
                    type="text"
                    value={formData.plate}
                    onChange={(e) => setFormData({ ...formData, plate: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN (Número de Identificación del Vehículo)
                </label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={17}
                />
              </div>

              {!editingVehicle && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
                    Establecer como vehículo predeterminado
                  </label>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  {editingVehicle ? 'Actualizar' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManager;
